import { useCallback } from 'react';
import { useQueryClient } from 'react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { useToastContext } from '@/contexts/toastContext';
import usePayrollProfileLinking from '@/hooks/usePayrollProfileLinking';
import usePayrollTransactionLinking from '@/hooks/usePayrollTransactionLinking';
import { useSeoFetch } from '@/hooks/useSeoFetch';
import { getActiveOrgId } from '@/lib/activeOrg';
import financePayrollService, { BulkReport, BulkReportItem } from '@/services/financePayroll';
import { FetchApiIds } from '@/types';
import { Paths } from '@/types/routes';
import FinanceXPayrollReportComponent from './FinanceXPayrollReport.component';

const FinanceXPayrollReportContainer = (): JSX.Element => {
  const { reportId = '' } = useParams();
  const orgId = getActiveOrgId();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToastContext();

  const { data: reports = [] } = useSeoFetch<BulkReport[]>(
    [FetchApiIds.FX_BULK_REPORTS, orgId],
    () => financePayrollService.getReports(),
    { retry: false }
  );

  const report = reports.find((r) => r.id === reportId || r.shortId === reportId) ?? null;

  const { data: items = [], isLoading } = useSeoFetch<BulkReportItem[]>(
    [FetchApiIds.FX_BULK_REPORT_ITEMS, reportId],
    () => financePayrollService.getReportItems(report?.id ?? reportId),
    { enabled: !!report, retry: false }
  );

  const { candidates, loadingCandidates, loadCandidates, linkTransaction, unlinkTransaction } =
    usePayrollTransactionLinking(reportId, report);

  const refreshItems = useCallback(() => {
    queryClient.invalidateQueries(FetchApiIds.FX_BULK_REPORT_ITEMS);
  }, [queryClient]);

  const {
    linkProfile: handleLinkProfile,
    spread,
    applySpread,
    dismissSpread,
    isApplying: isApplyingSpread,
  } = usePayrollProfileLinking(refreshItems);

  const handleDelete = useCallback(async () => {
    try {
      await financePayrollService.deleteReport(reportId);
      queryClient.invalidateQueries(FetchApiIds.FX_BULK_REPORTS);
      queryClient.invalidateQueries(FetchApiIds.FX_BULK_PROCESSORS);
      navigate(Paths.FINANCEX_PAYROLL);
    } catch {
      toast.showErrorFeedback('Failed to delete report.');
    }
  }, [reportId, queryClient, navigate, toast]);

  return (
    <FinanceXPayrollReportComponent
      report={report}
      items={items}
      isLoading={isLoading}
      candidates={candidates}
      loadingCandidates={loadingCandidates}
      onLoadCandidates={loadCandidates}
      onLinkTransaction={linkTransaction}
      onUnlinkTransaction={unlinkTransaction}
      onLinkProfile={handleLinkProfile}
      spread={spread}
      onApplySpread={applySpread}
      onDismissSpread={dismissSpread}
      isApplyingSpread={isApplyingSpread}
      onDelete={handleDelete}
    />
  );
};

export default FinanceXPayrollReportContainer;
